/**
 * RC10h · Round 5 — canonical active-navigation resolver.
 *
 * Single source of truth for which left-nav / mobile-nav item is
 * "active" for a given URL. Previously the sidebar and mobile shells
 * each computed activity independently using a broad
 * `pathname.startsWith(item.to + "/")` test. That co-highlighted
 * BOTH Fleet AND Asset Owners for any `/fleet/asset-owners*` path
 * because the former's prefix match swallowed the latter.
 *
 * This resolver fixes it with a longest-match-wins algorithm:
 * rules are declared most-specific-first, and the first match wins.
 * Every nav surface imports `resolveActiveNavKey(pathname)` and
 * compares the result against the nav item's explicit `navKey`.
 *
 * Rules in this file are the ONLY place active-state is defined.
 * Grep for `resolveActiveNavKey` across the frontend — there must
 * be exactly ONE definition (this file) and all nav renderers must
 * import it rather than reimplementing their own logic.
 */

/**
 * Rule order matters: nested / most-specific routes MUST appear
 * before their parent prefixes. The resolver returns the first
 * matching rule's `key`.
 */
export const NAV_KEY_RULES = [
  // Nested route under /fleet — MUST be evaluated before "fleet"
  {
    key: "asset-owners",
    test: (p) =>
      p === "/fleet/asset-owners" || p.startsWith("/fleet/asset-owners/"),
  },

  // Top-level routes
  {
    key: "dashboard",
    test: (p) => p === "/dashboard" || p.startsWith("/dashboard/"),
  },
  {
    key: "tours",
    test: (p) =>
      p === "/tours" ||
      p.startsWith("/tours/") ||
      p === "/schedule" ||
      p.startsWith("/schedule/"),
  },
  {
    key: "dispatch",
    test: (p) => p === "/dispatch" || p.startsWith("/dispatch/"),
  },
  {
    key: "clients",
    test: (p) =>
      p === "/customers" ||
      p.startsWith("/customers/") ||
      p === "/clients" ||
      p.startsWith("/clients/"),
  },
  {
    key: "people",
    test: (p) => p === "/contacts" || p.startsWith("/contacts/"),
  },
  // Fleet comes AFTER asset-owners so that any `/fleet/asset-owners*`
  // path is caught by the first rule above and never reaches this one.
  // Everything else under `/fleet` (asset list, `/fleet/:id`, `/fleet/map`)
  // activates Fleet.
  {
    key: "fleet",
    test: (p) => p === "/fleet" || p.startsWith("/fleet/"),
  },
  {
    key: "drivers",
    test: (p) => p === "/drivers" || p.startsWith("/drivers/"),
  },
  {
    key: "service",
    test: (p) =>
      p === "/service" ||
      p.startsWith("/service/") ||
      p === "/maintenance" ||
      p.startsWith("/maintenance/"),
  },
  {
    key: "quotes",
    test: (p) => p === "/quotes" || p.startsWith("/quotes/"),
  },
  {
    key: "billing",
    test: (p) => p === "/billing" || p.startsWith("/billing/"),
  },
  {
    key: "documents",
    test: (p) => p === "/documents" || p.startsWith("/documents/"),
  },
  {
    key: "reports",
    test: (p) => p === "/reports" || p.startsWith("/reports/"),
  },
];

/**
 * Resolve the single active nav key for the given pathname.
 * Returns `null` for unknown routes (e.g. `/login`, `/portal/*`,
 * `/administration/*` — the Administration group drives its own
 * expand state via `pathname.startsWith("/administration")` in
 * `Layout.jsx` because it's a toggle, not a NavLink).
 */
export const resolveActiveNavKey = (pathname) => {
  if (!pathname || typeof pathname !== "string") return null;
  const rule = NAV_KEY_RULES.find((r) => r.test(pathname));
  return rule ? rule.key : null;
};
